import * as XLSX from 'xlsx';

export const Z_MODE_DAILY = 'daily';
export const Z_MODE_MONTHLY = 'monthly';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const normHeader = (v) => String(v ?? '').replace(/[\s"'״׳.`]/g, '');

/** Normalized header text → field. Matched exactly so titles like 'דו"ח ריכוז Z' never hit. */
const HEADER_FIELDS = {
    מהםפטור: 'exempt',
    פטור: 'exempt',
    סהכ: 'total',
    מזומן: 'cash',
    שיקים: 'checks',
    שקים: 'checks',
    צקים: 'checks',
    כאשראי: 'creditCard',
    אשראי: 'creditCard',
    כרטיסיאשראי: 'creditCard',
    הקפה: 'onAccount',
    אחר: 'other',
    נקודותזיכוי: 'creditPoints',
    שוברים: 'vouchers',
    יתרתפתיחה: 'openingBalance',
    תאריך: 'date',
    קופה: 'register',
    מס: 'zNumber',
    מספר: 'zNumber',
    מסZ: 'zNumber',
};

const AMOUNT_FIELDS = ['total', 'exempt', 'cash', 'checks', 'creditCard', 'onAccount', 'other', 'vouchers', 'creditPoints'];

const toNumber = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const s = String(v ?? '').replace(/[,\s₪]/g, '');
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

/** Excel serial, Date or dd/mm/yyyy text → UTC-midnight Date (or null). */
const toUtcDate = (v) => {
    if (v instanceof Date && !Number.isNaN(v.getTime())) {
        return new Date(Date.UTC(v.getFullYear(), v.getMonth(), v.getDate()));
    }
    if (typeof v === 'number') {
        if (v < 20000 || v > 80000) return null;
        return new Date(Date.UTC(1899, 11, 30) + Math.floor(v) * 86400000);
    }
    const m = String(v ?? '').trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
    if (!m) return null;
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    const d = new Date(Date.UTC(year, Number(m[2]) - 1, Number(m[1])));
    return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Crystal-Reports exports place header labels a column or two off their data column,
 * so each header is assigned to the nearest column that actually holds data.
 */
const assignColumns = (headers, dataColumns) => {
    const pairs = [];
    headers.forEach((h) => {
        dataColumns.forEach((c) => {
            const dist = Math.abs(h.col - c);
            if (dist <= 3) pairs.push({ field: h.field, col: c, dist });
        });
    });
    pairs.sort((a, b) => a.dist - b.dist);
    const map = {};
    const usedCols = new Set();
    pairs.forEach(({ field, col }) => {
        if (map[field] != null || usedCols.has(col)) return;
        map[field] = col;
        usedCols.add(col);
    });
    return map;
};

/** Parse a uPOS / cash-register "דו"ח ריכוז Z" sheet (xls/xlsx) into one record per Z. */
export const parseZReportWorkbook = (data) => {
    const wb = XLSX.read(data, { type: 'array', cellDates: false });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (!ws) throw new Error('EmptyFile');
    const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });

    const headerRow = grid.findIndex((row) => {
        const cells = row.map(normHeader);
        return cells.includes('מזומן') && cells.includes('תאריך');
    });
    if (headerRow < 0) throw new Error('ZReportHeaderNotFound');

    const headers = [];
    const seen = new Set();
    [headerRow, headerRow - 1].forEach((r) => {
        (grid[r] || []).forEach((cell, col) => {
            const field = HEADER_FIELDS[normHeader(cell)];
            if (!field || seen.has(field)) return;
            seen.add(field);
            headers.push({ field, col });
        });
    });

    const body = grid.slice(headerRow + 1);
    const dataColumns = new Set();
    body.forEach((row) => row.forEach((cell, col) => {
        if (toNumber(cell) != null) dataColumns.add(col);
    }));
    const columns = assignColumns(headers, [...dataColumns]);
    if (columns.date == null || columns.total == null) throw new Error('ZReportHeaderNotFound');

    const rows = [];
    body.forEach((row) => {
        const date = toUtcDate(row[columns.date]);
        const total = toNumber(row[columns.total]);
        if (!date || total == null) return;
        const rec = { date };
        AMOUNT_FIELDS.forEach((f) => {
            rec[f] = columns[f] != null ? round2(toNumber(row[columns[f]]) ?? 0) : 0;
        });
        rec.zNumber = columns.zNumber != null ? String(row[columns.zNumber] ?? '').trim() : '';
        rec.register = columns.register != null ? String(row[columns.register] ?? '').trim() : '';
        const paid = rec.cash + rec.checks + rec.creditCard + rec.onAccount + rec.other + rec.vouchers + rec.creditPoints;
        rec.mismatch = round2(rec.total - paid);
        rows.push(rec);
    });
    if (!rows.length) throw new Error('ZReportNoRows');

    let companyId = '';
    grid.slice(0, headerRow).some((row) => row.some((cell) => {
        const m = String(cell ?? '').match(/(?:ע\.?\s?מ|ח\.?\s?פ)\.?\s*(\d{5,9})/);
        if (m) companyId = m[1];
        return !!m;
    }));

    return { rows, companyId };
};

const pad2 = (n) => String(n).padStart(2, '0');
export const formatUtcDate = (d) => `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
const monthKey = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;

const sumRows = (rows) => {
    const s = {};
    AMOUNT_FIELDS.forEach((f) => { s[f] = round2(rows.reduce((acc, r) => acc + r[f], 0)); });
    return s;
};

export const summarizeZRows = (rows) => sumRows(rows);

/**
 * One group per Z (daily) or per calendar month (monthly). Each group yields:
 * the income entry (customers → income, VAT 100%), an exempt part when present (VAT 0),
 * and one receipt entry per payment method (method card → customers).
 */
export const buildZReportEntries = (rows, { mode, cards, sortCodes, labels }) => {
    let groups;
    if (mode === Z_MODE_MONTHLY) {
        const byMonth = new Map();
        rows.forEach((r) => {
            const key = monthKey(r.date);
            if (!byMonth.has(key)) byMonth.set(key, []);
            byMonth.get(key).push(r);
        });
        groups = [...byMonth.values()].map((list) => {
            const date = new Date(Math.max(...list.map((r) => r.date.getTime())));
            const zs = list.map((r) => r.zNumber).filter(Boolean);
            const reference = zs.length > 1 ? `${zs[0]}-${zs[zs.length - 1]}` : (zs[0] || '');
            return {
                date,
                reference,
                description: `${labels.monthlySummary} ${pad2(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`,
                sums: sumRows(list),
            };
        });
    } else {
        groups = rows.map((r) => ({
            date: r.date,
            reference: r.zNumber,
            description: [`${labels.zReport} ${r.zNumber}`.trim(), r.register ? `${labels.register} ${r.register}` : '']
                .filter(Boolean)
                .join(' - '),
            sums: sumRows([r]),
        }));
    }

    const entries = [];
    groups.forEach((g, groupIndex) => {
        const base = { groupIndex, date: g.date, reference: g.reference, description: g.description };
        const taxable = round2(g.sums.total - g.sums.exempt);
        const push = (kind, debitAccount, creditAccount, sortCodeId, vat, amount) => {
            if (round2(amount) === 0) return;
            entries.push({ ...base, kind, debitAccount, creditAccount, sortCodeId, vat, amount: round2(amount) });
        };
        push('income', cards.customers, cards.income, sortCodes.income, 100, taxable);
        push('exempt', cards.customers, cards.income, sortCodes.exempt, 0, g.sums.exempt);
        push('cash', cards.cash, cards.customers, sortCodes.debitCredit, 0, g.sums.cash);
        push('checks', cards.checks, cards.customers, sortCodes.debitCredit, 0, g.sums.checks);
        push('creditCard', cards.creditCard, cards.customers, sortCodes.debitCredit, 0, g.sums.creditCard);
    });
    return entries;
};
